import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { USCIS_COLORS } from "../styles/colors";

// USCIS color standards
const USCIS_BLUE = USCIS_COLORS.primary;
const USCIS_RED = USCIS_COLORS.secondary;

// Theme preferences type
type ThemePreferences = {
  mode: 'light' | 'dark' | 'system';
  highContrast: boolean;
  fontSize: 'small' | 'medium' | 'large';
  primaryColor: string;
  showGovernmentBanner: boolean;
};

// Context value type
type ThemeContextType = {
  themePreferences: ThemePreferences;
  updateThemePreferences: (preferences: Partial<ThemePreferences>) => void;
  theme: Theme;
};

// Create context with proper typing
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Custom hook to use theme context
export const useCustomTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useCustomTheme must be used within a ThemeProvider');
  }
  return context;
};

// Backwards compatibility alias
export const useTheme = useCustomTheme;

// Theme provider component
export const CustomThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [themePreferences, setThemePreferences] = useState<ThemePreferences>({
    mode: 'light',
    highContrast: false,
    fontSize: 'medium',
    primaryColor: USCIS_BLUE,
    showGovernmentBanner: false,
  });

  // Effect to detect system preference changes
  useEffect(() => {
    if (themePreferences.mode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => setThemePreferences(prev => ({ ...prev }));
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themePreferences.mode]);
  
  // Update theme preferences
  const updateThemePreferences = (preferences: Partial<ThemePreferences>) => {
    setThemePreferences(prev => {
      const newPreferences = { ...prev, ...preferences };
      
      // Force a re-render of components using this theme
      document.body.style.fontSize = newPreferences.fontSize === 'small' ? '14px' : 
                                   newPreferences.fontSize === 'large' ? '18px' : '16px';
      
      return newPreferences;
    });
  };

  // Determine actual mode based on preferences
  const actualMode = themePreferences.mode === 'system' 
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : themePreferences.mode;

  // Create Material-UI theme based on preferences
  const theme = createTheme({
    palette: {
      mode: actualMode as 'light' | 'dark',
      primary: {
        main: themePreferences.primaryColor,
        dark: themePreferences.primaryColor === USCIS_BLUE ? '#002244' : undefined,
      },
      secondary: {
        main: USCIS_RED,
      },
      background: {
        default: themePreferences.highContrast ? '#FFFFFF' : '#F5F6F7',
        paper: '#FFFFFF',
      },
      text: {
        primary: themePreferences.highContrast ? '#000000' : '#0C1F3F',
        secondary: themePreferences.highContrast ? '#000000' : '#2D3436',
      },
      error: {
        main: USCIS_RED,
      },
    },
    typography: {
      fontFamily: [
        'sourceSansPro',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      fontSize: themePreferences.fontSize === 'small' ? 14 : 
                themePreferences.fontSize === 'large' ? 18 : 16,
      h1: {
        fontWeight: 600,
        fontSize: themePreferences.fontSize === 'small' ? '2.2rem' : 
                 themePreferences.fontSize === 'large' ? '3rem' : '2.5rem',
      },
      h2: {
        fontWeight: 600,
        fontSize: themePreferences.fontSize === 'small' ? '1.8rem' : 
                 themePreferences.fontSize === 'large' ? '2.4rem' : '2rem',
      },
      button: {
        textTransform: 'none' as const,
      },
    },
  });

  // Apply CSS custom properties for font scaling
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--app-font-size-base', 
      themePreferences.fontSize === 'small' ? '14px' : 
      themePreferences.fontSize === 'large' ? '18px' : '16px'
    );
    
    document.documentElement.style.setProperty(
      '--app-font-scale-factor', 
      themePreferences.fontSize === 'small' ? '0.875' : 
      themePreferences.fontSize === 'large' ? '1.125' : '1'
    );
    
    // Apply a class to the body for potential CSS targeting
    document.body.className = `theme-${actualMode}-${themePreferences.highContrast ? 'high-contrast' : 'normal'}`;
  }, [themePreferences.fontSize, themePreferences.highContrast, actualMode]);
  
  // Return provider with theme
  return (
    <ThemeContext.Provider value={{ themePreferences, updateThemePreferences, theme }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
